import { format } from "date-fns"

import type { AugmentedFeedback } from "@productlane/api/src/lib/timeline-events"
import {
	asBlockHtml,
	buildQuoteAttribution,
	convertNewlinesToBr,
} from "@productlane/lib/src/html"
import { formatEmailContacts } from "@productlane/ui"

import { emailBody } from "@/lib/email-body"
import { EMAIL_QUOTE_STYLE, wrapInQuote } from "./email-reply-scaffold"

type ThreadEmail = NonNullable<AugmentedFeedback["emails"]>[number]

/**
 * The quoted history under a reply: the forwarded email, a finished live chat,
 * the latest email, or the thread's opening text. "" when there is nothing.
 */
export function quotedReplyContent({
	thread,
	latestEmail,
	emailCount,
	isForwardMode,
}: {
	thread: Pick<
		AugmentedFeedback,
		"origin" | "liveChatSession" | "contact" | "createdAt" | "text" | "title"
	>
	latestEmail: ThreadEmail | undefined
	emailCount: number
	isForwardMode: boolean
}): string {
	const currentLiveChat = thread.liveChatSession
	let quotedContent = ""

	if (isForwardMode && latestEmail?.from) {
		const sentAtDay = new Date(latestEmail.createdAt).toLocaleDateString(
			"en-US",
			{
				weekday: "short",
				year: "numeric",
				month: "short",
				day: "numeric",
			},
		)
		const sentAtTime = new Date(latestEmail.createdAt).toLocaleTimeString(
			"en-US",
			{
				hour: "numeric",
				minute: "2-digit",
			},
		)

		const fromContact = formatEmailContacts(latestEmail.from)[0]
		const senderName = fromContact?.name || "Unknown"
		const senderEmail = fromContact?.email || "unknown@example.com"

		quotedContent = `<p>---------- Forwarded message ----------</p>
<p>From: ${senderName} &lt;${senderEmail}&gt;</p>
<p>Date: ${sentAtDay} at ${sentAtTime}</p>
<p>Subject: ${thread.title || "Feedback"}</p>
<p></p>
<blockquote style="${EMAIL_QUOTE_STYLE}">${emailBody(latestEmail).html}</blockquote>`
	} else if (
		thread.origin === "LIVE_CHAT" &&
		!!currentLiveChat?.isFinished &&
		currentLiveChat.messages?.length > 0 &&
		emailCount === 0
	) {
		const liveChatMessages = currentLiveChat.messages
			.filter((msg) => msg.role !== "SYSTEM")
			.map((msg) => {
				const sentAtDay = format(new Date(msg.createdAt), "eee, MMM dd, yyyy")
				const sentAtTime = format(new Date(msg.createdAt), "p")
				const senderName =
					msg.name || msg.email || (msg.role === "AGENT" ? "Agent" : "Customer")

				return `${buildQuoteAttribution({
					day: sentAtDay,
					time: sentAtTime,
					name: senderName,
				})}
<p>${convertNewlinesToBr(msg.content)}</p>`
			})
			.join("<br>")

		quotedContent = `<blockquote class="productlane-quote" style="${EMAIL_QUOTE_STYLE}"><p>Live Chat Conversation:</p>
${liveChatMessages}</blockquote>`
	} else if (latestEmail?.from) {
		const sentAtDay = new Date(latestEmail.createdAt).toLocaleDateString(
			"en-US",
			{
				weekday: "short",
				year: "numeric",
				month: "short",
				day: "numeric",
			},
		)
		const sentAtTime = new Date(latestEmail.createdAt).toLocaleTimeString(
			"en-US",
			{
				hour: "numeric",
				minute: "2-digit",
			},
		)

		const fromContact = formatEmailContacts(latestEmail.from)[0]
		const senderName = fromContact?.name || "Unknown"
		const senderEmail = fromContact?.email || "unknown@example.com"

		quotedContent = `${buildQuoteAttribution({
			day: sentAtDay,
			time: sentAtTime,
			name: senderName,
			email: senderEmail,
		})}
<blockquote style="${EMAIL_QUOTE_STYLE}">${emailBody(latestEmail).html}</blockquote>`
	} else if (thread.text && thread.origin !== "INAPP") {
		const contactName = thread.contact?.name || undefined
		const contactEmail = thread.contact?.email || undefined
		const sentAtDay = new Date(thread.createdAt).toLocaleDateString("en-US", {
			weekday: "short",
			year: "numeric",
			month: "short",
			day: "numeric",
		})
		const sentAtTime = new Date(thread.createdAt).toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
		})

		quotedContent = `${buildQuoteAttribution({
			day: sentAtDay,
			time: sentAtTime,
			name: contactName || contactEmail || "Unknown",
			email: contactName ? contactEmail : undefined,
		})}
${wrapInQuote(asBlockHtml(thread.text))}`
	}

	return quotedContent
}
